import React, { useState, useEffect } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { cartonService } from '../../services/cartonService';
import { barrelService } from '../../services/barrelService';
import { employeeService } from '../../services/employeeService';
import { Truck, Plus, Trash2, Search, RefreshCw, X, Save, AlertTriangle, CheckCircle, Package, Wine, ChevronDown, ChevronUp, Minus } from 'lucide-react';

const STATUS_LABELS = {
  planned: { label: 'Planifié', color: 'bg-blue-100 text-blue-700' },
  loading: { label: 'En chargement', color: 'bg-yellow-100 text-yellow-700' },
  loaded: { label: 'Chargé', color: 'bg-purple-100 text-purple-700' },
  departed: { label: 'Parti', color: 'bg-orange-100 text-orange-700' },
  arrived: { label: 'Arrivé', color: 'bg-green-100 text-green-700' }
};

const BARREL_TYPES_CONFIG = [
  { value: 'noir_270l', label: 'Barrique Noire 270L' },
  { value: 'bleu_220l', label: 'Barrique Bleue 220L' }
];

const defaultSessionForm = {
  truckId: '',
  driverName: '',
  destination: '',
  loadingDate: new Date()?.toISOString()?.split('T')?.[0],
  notes: ''
};

export default function TruckLoadingManagement() {
  const [sessions, setSessions] = useState([]);
  const [cartonInventory, setCartonInventory] = useState([]);
  const [barrelInventory, setBarrelInventory] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionForm, setSessionForm] = useState(defaultSessionForm);
  // Items to load in the new session modal
  const [loadingItems, setLoadingItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedSession, setExpandedSession] = useState(null);
  const [showItemModal, setShowItemModal] = useState(null);
  const [itemForm, setItemForm] = useState({ itemType: 'carton', barrelType: '', cartonInventoryId: '', cartonType: '', quantity: 1, notes: '' });
  const [addingItem, setAddingItem] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sess, inv, barrels, emps] = await Promise.all([
        cartonService?.getTruckLoadingSessions(),
        cartonService?.getCartonInventory(),
        barrelService?.getInventory(),
        employeeService?.getAll({ isActive: true })
      ]);
      setSessions(sess);
      setCartonInventory(inv);
      setBarrelInventory(barrels);
      // Filter drivers: role = chauffeur AND zone contains France (or zone = France)
      const driverList = emps?.filter(e =>
        e?.role?.toLowerCase()?.includes('chauffeur') &&
        e?.zone?.toLowerCase()?.includes('france')
      );
      setDrivers(driverList);
    } catch (e) {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = sessions?.filter(s => {
    const matchSearch = s?.destination?.toLowerCase()?.includes(search?.toLowerCase()) ||
      s?.reference?.toLowerCase()?.includes(search?.toLowerCase()) ||
      s?.driverName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      s?.truckId?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = !filterStatus || s?.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // ---- Loading items management in modal ----
  const getBarrelStock = (barrelType) => {
    return barrelInventory?.find(b => b?.barrelType === barrelType)?.stockQuantity || 0;
  };

  const getCartonStock = (cartonInventoryId) => {
    return cartonInventory?.find(c => c?.id === cartonInventoryId)?.stockQuantity || 0;
  };

  const getItemAlreadyLoaded = (type, id) => {
    return loadingItems?.filter(i => {
      if (type === 'barrel') return i?.itemType === 'barrel' && i?.barrelType === id;
      return i?.itemType === 'carton' && i?.cartonInventoryId === id;
    })?.reduce((sum, i) => sum + i?.quantity, 0);
  };

  const getAvailableStock = (type, id) => {
    const stock = type === 'barrel' ? getBarrelStock(id) : getCartonStock(id);
    const alreadyLoaded = getItemAlreadyLoaded(type, id);
    return stock - alreadyLoaded;
  };

  const handleAddLoadingItem = (type, id, label, cartonType) => {
    const available = getAvailableStock(type, id);
    if (available <= 0) return;
    setLoadingItems(prev => {
      const existing = prev?.find(i =>
        type === 'barrel' ? (i?.itemType === 'barrel' && i?.barrelType === id) : (i?.itemType === 'carton' && i?.cartonInventoryId === id)
      );
      if (existing) {
        return prev?.map(i => {
          if (type === 'barrel' ? (i?.itemType === 'barrel' && i?.barrelType === id) : (i?.itemType === 'carton' && i?.cartonInventoryId === id)) {
            return { ...i, quantity: Math.min(i?.quantity + 1, getBarrelStock(id) || getCartonStock(id)) };
          }
          return i;
        });
      }
      return [...prev, {
        itemType: type,
        barrelType: type === 'barrel' ? id : null,
        cartonInventoryId: type === 'carton' ? id : null,
        cartonType: cartonType || null,
        label,
        quantity: 1,
        notes: ''
      }];
    });
  };

  const handleChangeItemQty = (type, id, delta) => {
    setLoadingItems(prev => {
      return prev?.map(i => {
        const match = type === 'barrel' ? (i?.itemType === 'barrel' && i?.barrelType === id) : (i?.itemType === 'carton' && i?.cartonInventoryId === id);
        if (!match) return i;
        const available = getAvailableStock(type, id) + i?.quantity; // add back current qty
        const newQty = Math.max(1, Math.min(i?.quantity + delta, available));
        return { ...i, quantity: newQty };
      })?.filter(i => i?.quantity > 0);
    });
  };

  const handleRemoveLoadingItem = (type, id) => {
    setLoadingItems(prev => prev?.filter(i => {
      if (type === 'barrel') return !(i?.itemType === 'barrel' && i?.barrelType === id);
      return !(i?.itemType === 'carton' && i?.cartonInventoryId === id);
    }));
  };

  const handleCreateSession = async () => {
    if (!sessionForm?.destination) return;
    setSaving(true);
    setError('');
    try {
      await cartonService?.createTruckLoadingSessionWithItems(sessionForm, loadingItems);
      setShowSessionModal(false);
      setSessionForm(defaultSessionForm);
      setLoadingItems([]);
      setSuccess('Session de chargement créée et stock mis à jour');
      fetchData();
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError('Erreur lors de la création : ' + (e?.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await cartonService?.updateTruckLoadingStatus(id, status);
      fetchData();
    } catch (e) {
      setError('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteSession = async (id) => {
    if (!window.confirm('Supprimer cette session ? Les stocks cartons seront restaurés.')) return;
    try {
      await cartonService?.deleteTruckLoadingSession(id);
      fetchData();
    } catch (e) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleAddItem = async () => {
    if (!showItemModal || itemForm?.quantity < 1) return;
    if (itemForm?.itemType === 'barrel' && !itemForm?.barrelType) return;
    if (itemForm?.itemType === 'carton' && !itemForm?.cartonInventoryId) return;
    setAddingItem(true);
    try {
      const cartonItem = cartonInventory?.find(i => i?.id === itemForm?.cartonInventoryId);
      await cartonService?.addTruckLoadingItem(showItemModal, {
        ...itemForm,
        cartonType: cartonItem?.cartonType || itemForm?.cartonType
      });
      setShowItemModal(null);
      setItemForm({ itemType: 'carton', barrelType: '', cartonInventoryId: '', cartonType: '', quantity: 1, notes: '' });
      setSuccess('Article ajouté au chargement');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Erreur lors de l\'ajout de l\'article');
    } finally {
      setAddingItem(false);
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Retirer cet article ? Le stock sera restauré si c\'est un carton.')) return;
    try {
      await cartonService?.removeTruckLoadingItem(itemId);
      fetchData();
    } catch (e) {
      setError('Erreur lors du retrait');
    }
  };

  const getTotalItems = (session) => {
    return session?.items?.reduce((sum, i) => sum + (i?.quantity || 0), 0);
  };

  const openNewSessionModal = () => {
    setSessionForm(defaultSessionForm);
    setLoadingItems([]);
    setError('');
    setShowSessionModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Truck className="h-7 w-7 text-amber-600" />
              Chargement Camions
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Gestion des chargements — barriques & cartons</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700">
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
            <button onClick={openNewSessionModal}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium">
              <Plus className="h-4 w-4" /> Nouveau chargement
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
            <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <CheckCircle className="h-4 w-4" /> {success}
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {Object.entries(STATUS_LABELS)?.map(([key, val]) => (
            <div key={key} className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">{val?.label}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {sessions?.filter(s => s?.status === key)?.length}
              </p>
            </div>
          ))}
        </div>

        {/* Info Banner */}
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
          <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
            ℹ️ Lors du chargement, le stock de barriques et cartons est automatiquement défalqué.
            Si vous supprimez un chargement, le stock cartons est restauré.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" placeholder="Rechercher par destination, camion, chauffeur..." value={search} onChange={e => setSearch(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)}
            className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_LABELS)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
          </select>
        </div>

        {/* Sessions List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">Chargement...</div>
        ) : filtered?.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">Aucune session trouvée</div>
        ) : (
          <div className="space-y-4">
            {filtered?.map(session => (
              <div key={session?.id} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{session?.reference}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${STATUS_LABELS?.[session?.status]?.color}`}>
                        {STATUS_LABELS?.[session?.status]?.label}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      🚛 {session?.destination}
                    </h3>
                    <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {session?.truckId && <span>🚚 {session?.truckId}</span>}
                      {session?.driverName && <span>👤 {session?.driverName}</span>}
                      <span>📅 {session?.loadingDate}</span>
                      <span className="font-medium text-amber-600">{getTotalItems(session)} articles chargés</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={session?.status}
                      onChange={e => handleUpdateStatus(session?.id, e?.target?.value)}
                      className="text-xs border border-gray-300 dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    >
                      {Object.entries(STATUS_LABELS)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
                    </select>
                    <button
                      onClick={() => { setShowItemModal(session?.id); setItemForm({ itemType: 'carton', barrelType: '', cartonInventoryId: '', cartonType: '', quantity: 1, notes: '' }); }}
                      className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700"
                    >
                      <Plus className="h-3 w-3" /> Ajouter
                    </button>
                    <button
                      onClick={() => setExpandedSession(expandedSession === session?.id ? null : session?.id)}
                      className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      {expandedSession === session?.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    <button onClick={() => handleDeleteSession(session?.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {expandedSession === session?.id && (
                  <div className="border-t border-gray-100 dark:border-slate-700">
                    {session?.items?.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        Aucun article chargé. Cliquez sur "Ajouter" pour charger des barriques ou cartons.
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-slate-700/50">
                          <tr>
                            <th className="text-left px-4 py-2 text-gray-600 dark:text-gray-300 font-medium">Type</th>
                            <th className="text-left px-4 py-2 text-gray-600 dark:text-gray-300 font-medium">Article</th>
                            <th className="text-right px-4 py-2 text-gray-600 dark:text-gray-300 font-medium">Quantité</th>
                            <th className="text-left px-4 py-2 text-gray-600 dark:text-gray-300 font-medium">Notes</th>
                            <th className="text-center px-4 py-2 text-gray-600 dark:text-gray-300 font-medium">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                          {session?.items?.map(item => (
                            <tr key={item?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30">
                              <td className="px-4 py-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${
                                  item?.item_type === 'barrel' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {item?.item_type === 'barrel' ? <Wine className="h-3 w-3" /> : <Package className="h-3 w-3" />}
                                  {item?.item_type === 'barrel' ? 'Barrique' : 'Carton'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                                {item?.item_type === 'barrel'
                                  ? (BARREL_TYPES_CONFIG?.find(b => b?.value === item?.barrel_type)?.label || item?.barrel_type)
                                  : item?.carton_type}
                              </td>
                              <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-white">{item?.quantity}</td>
                              <td className="px-4 py-2 text-gray-500 dark:text-gray-400 text-xs">{item?.notes || '-'}</td>
                              <td className="px-4 py-2 text-center">
                                <button onClick={() => handleRemoveItem(item?.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== NEW SESSION MODAL ===== */}
      {showSessionModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Truck className="h-5 w-5 text-amber-600" /> Nouveau chargement camion
              </h2>
              <button onClick={() => setShowSessionModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              {/* Destination */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destination *</label>
                <input type="text" placeholder="ex: Abidjan, Dakar..." value={sessionForm?.destination} onChange={e => setSessionForm(f => ({ ...f, destination: e?.target?.value }))}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
              </div>

              {/* Truck + Driver */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Immatriculation camion</label>
                  <input type="text" placeholder="ex: AB-123-CD" value={sessionForm?.truckId} onChange={e => setSessionForm(f => ({ ...f, truckId: e?.target?.value }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chauffeur</label>
                  <select value={sessionForm?.driverName} onChange={e => setSessionForm(f => ({ ...f, driverName: e?.target?.value }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                    <option value="">Sélectionner un chauffeur...</option>
                    {drivers?.length === 0 && (
                      <option disabled value="">Aucun chauffeur disponible en France</option>
                    )}
                    {drivers?.map(d => (
                      <option key={d?.id} value={d?.name}>{d?.name} {d?.zone ? `— ${d?.zone}` : ''}</option>
                    ))}
                  </select>
                  {drivers?.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">⚠️ Aucun employé chauffeur actif en France trouvé</p>
                  )}
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de chargement</label>
                <input type="date" value={sessionForm?.loadingDate} onChange={e => setSessionForm(f => ({ ...f, loadingDate: e?.target?.value }))}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
              </div>

              {/* ===== ARTICLES A CHARGER ===== */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                  <Package className="h-4 w-4 text-amber-600" /> Articles à charger
                </h3>

                {/* Barriques */}
                <div className="mb-3">
                  <p className="text-xs font-semibold text-purple-700 dark:text-purple-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Wine className="h-3.5 w-3.5" /> Barriques
                  </p>
                  <div className="space-y-2">
                    {BARREL_TYPES_CONFIG?.map(bt => {
                      const stock = getBarrelStock(bt?.value);
                      const loaded = getItemAlreadyLoaded('barrel', bt?.value);
                      const available = stock - loaded;
                      const isInList = loadingItems?.some(i => i?.itemType === 'barrel' && i?.barrelType === bt?.value);
                      const currentQty = loadingItems?.find(i => i?.itemType === 'barrel' && i?.barrelType === bt?.value)?.quantity || 0;
                      return (
                        <div key={bt?.value} className={`flex items-center justify-between p-3 rounded-lg border ${
                          stock === 0 ? 'border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800 opacity-60' : isInList ?'border-purple-300 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-700': 'border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/30'
                        }`}>
                          <div>
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{bt?.label}</p>
                            <p className={`text-xs ${stock === 0 ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'}`}>
                              Stock disponible : <span className="font-semibold">{available}</span>
                              {stock === 0 && ' — Stock épuisé'}
                            </p>
                          </div>
                          {stock === 0 ? (
                            <span className="text-xs text-red-600 font-medium px-2 py-1 bg-red-100 rounded-lg">Indisponible</span>
                          ) : isInList ? (
                            <div className="flex items-center gap-2">
                              <button onClick={() => handleChangeItemQty('barrel', bt?.value, -1)}
                                className="w-7 h-7 flex items-center justify-center rounded-full border border-purple-300 text-purple-700 hover:bg-purple-100">
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-8 text-center text-sm font-bold text-purple-700 dark:text-purple-300">{currentQty}</span>
                              <button onClick={() => available > 0 && handleChangeItemQty('barrel', bt?.value, 1)}
                                disabled={available <= 0}
                                className="w-7 h-7 flex items-center justify-center rounded-full border border-purple-300 text-purple-700 hover:bg-purple-100 disabled:opacity-40">
                                <Plus className="h-3 w-3" />
                              </button>
                              <button onClick={() => handleRemoveLoadingItem('barrel', bt?.value)}
                                className="ml-1 p-1 text-red-400 hover:text-red-600">
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => handleAddLoadingItem('barrel', bt?.value, bt?.label, null)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700">
                              <Plus className="h-3 w-3" /> Ajouter
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Cartons */}
                <div>
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Package className="h-3.5 w-3.5" /> Cartons
                  </p>
                  {cartonInventory?.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">Aucun carton en inventaire</p>
                  ) : (
                    <div className="space-y-2">
                      {cartonInventory?.map(ct => {
                        const stock = ct?.stockQuantity || 0;
                        const loaded = getItemAlreadyLoaded('carton', ct?.id);
                        const available = stock - loaded;
                        const isInList = loadingItems?.some(i => i?.itemType === 'carton' && i?.cartonInventoryId === ct?.id);
                        const currentQty = loadingItems?.find(i => i?.itemType === 'carton' && i?.cartonInventoryId === ct?.id)?.quantity || 0;
                        return (
                          <div key={ct?.id} className={`flex items-center justify-between p-3 rounded-lg border ${
                            stock === 0 ? 'border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800 opacity-60' : isInList ?'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700': 'border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/30'
                          }`}>
                            <div>
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                {ct?.cartonType} {ct?.dimensions ? <span className="text-xs text-gray-500">({ct?.dimensions})</span> : ''}
                              </p>
                              <p className={`text-xs ${stock === 0 ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'}`}>
                                Stock disponible : <span className="font-semibold">{available}</span>
                                {stock === 0 && ' — Stock épuisé'}
                              </p>
                            </div>
                            {stock === 0 ? (
                              <span className="text-xs text-red-600 font-medium px-2 py-1 bg-red-100 rounded-lg">Indisponible</span>
                            ) : isInList ? (
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleChangeItemQty('carton', ct?.id, -1)}
                                  className="w-7 h-7 flex items-center justify-center rounded-full border border-amber-300 text-amber-700 hover:bg-amber-100">
                                  <Minus className="h-3 w-3" />
                                </button>
                                <span className="w-8 text-center text-sm font-bold text-amber-700 dark:text-amber-300">{currentQty}</span>
                                <button onClick={() => available > 0 && handleChangeItemQty('carton', ct?.id, 1)}
                                  disabled={available <= 0}
                                  className="w-7 h-7 flex items-center justify-center rounded-full border border-amber-300 text-amber-700 hover:bg-amber-100 disabled:opacity-40">
                                  <Plus className="h-3 w-3" />
                                </button>
                                <button onClick={() => handleRemoveLoadingItem('carton', ct?.id)}
                                  className="ml-1 p-1 text-red-400 hover:text-red-600">
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ) : (
                              <button onClick={() => handleAddLoadingItem('carton', ct?.id, ct?.cartonType, ct?.cartonType)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700">
                                <Plus className="h-3 w-3" /> Ajouter
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Summary of items to load */}
              {loadingItems?.length > 0 && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                  <p className="text-xs font-semibold text-green-800 dark:text-green-300 mb-2">✅ Récapitulatif du chargement :</p>
                  <ul className="space-y-1">
                    {loadingItems?.map((item, idx) => (
                      <li key={idx} className="text-xs text-green-700 dark:text-green-400 flex justify-between">
                        <span>{item?.label || item?.cartonType || item?.barrelType}</span>
                        <span className="font-bold">{item?.quantity} unité{item?.quantity > 1 ? 's' : ''}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea value={sessionForm?.notes} onChange={e => setSessionForm(f => ({ ...f, notes: e?.target?.value }))} rows={2}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-slate-700 flex-shrink-0">
              <button onClick={() => setShowSessionModal(false)} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300">
                Annuler
              </button>
              <button onClick={handleCreateSession} disabled={saving || !sessionForm?.destination}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? 'Création...' : 'Créer le chargement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== ADD ITEM MODAL (for existing sessions) ===== */}
      {showItemModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Ajouter un article au chargement</h2>
              <button onClick={() => setShowItemModal(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Type d'article</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setItemForm(f => ({ ...f, itemType: 'barrel', cartonInventoryId: '', cartonType: '' }))}
                    className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      itemForm?.itemType === 'barrel' ? 'border-purple-500 bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300' : 'border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    <Wine className="h-4 w-4" /> Barrique
                  </button>
                  <button
                    onClick={() => setItemForm(f => ({ ...f, itemType: 'carton', barrelType: '' }))}
                    className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      itemForm?.itemType === 'carton' ? 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300' : 'border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    <Package className="h-4 w-4" /> Carton
                  </button>
                </div>
              </div>

              {itemForm?.itemType === 'barrel' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de barrique *</label>
                  <select value={itemForm?.barrelType} onChange={e => setItemForm(f => ({ ...f, barrelType: e?.target?.value }))}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                    <option value="">Sélectionner...</option>
                    {BARREL_TYPES_CONFIG?.map(bt => {
                      const stock = getBarrelStock(bt?.value);
                      return (
                        <option key={bt?.value} value={bt?.value} disabled={stock <= 0}>
                          {bt?.label} — Stock: {stock}{stock <= 0 ? ' (épuisé)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {itemForm?.itemType === 'carton' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de carton (depuis stock) *</label>
                  <select value={itemForm?.cartonInventoryId} onChange={e => {
                    const item = cartonInventory?.find(i => i?.id === e?.target?.value);
                    setItemForm(f => ({ ...f, cartonInventoryId: e?.target?.value, cartonType: item?.cartonType || '' }));
                  }}
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                    <option value="">Sélectionner...</option>
                    {cartonInventory?.map(i => (
                      <option key={i?.id} value={i?.id} disabled={i?.stockQuantity <= 0}>
                        {i?.cartonType} {i?.dimensions ? `(${i?.dimensions})` : ''} — Stock: {i?.stockQuantity}{i?.stockQuantity <= 0 ? ' (épuisé)' : ''}
                      </option>
                    ))}
                  </select>
                  {itemForm?.cartonInventoryId && (
                    <p className="text-xs text-amber-600 mt-1">⚠️ Le stock sera automatiquement défalqué lors de l'ajout</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quantité *</label>
                <input type="number" min="1" value={itemForm?.quantity} onChange={e => setItemForm(f => ({ ...f, quantity: parseInt(e?.target?.value) || 1 }))}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <input type="text" value={itemForm?.notes} onChange={e => setItemForm(f => ({ ...f, notes: e?.target?.value }))}
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white" />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-slate-700">
              <button onClick={() => setShowItemModal(null)} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300">
                Annuler
              </button>
              <button onClick={handleAddItem} disabled={addingItem}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50">
                <Plus className="h-4 w-4" /> {addingItem ? 'Ajout...' : 'Ajouter au chargement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
